import type { CSSProperties, ReactNode } from 'react';
import { useId, useState } from 'react';

import { cn } from '../cn';
import { Button, FilterPopover } from '../Input2';
import { cleanId } from '../Input2/types';
import { Alerts, Arrows, Maps, Media } from '../StrokeIcons';

export type SwitcherCampaign = {
  id: string;
  /** Display name of the campaign (cadence). */
  name: string;
  /** Cadence state — drives the row's leading icon. */
  state: string;
};

export type CampaignSwitcherPureProps = {
  /** Active campaigns for the contact (already filtered to in-progress states). */
  campaigns: SwitcherCampaign[];
  /** The campaign currently shown in the detail view, highlighted in the menu. */
  selectedCampaignId: string | null;
  /** Switch the detail view to another campaign. */
  onSelectCampaign: (id: string) => void;
  className?: string;
};

/** Small leading icon per cadence state (mirrors CampaignButton's state icons). */
function stateIcon(state: string): ReactNode {
  switch (state) {
    case 'paused':
      return <Media.PauseCircle />;
    case 'action-required':
    case 'pending-review':
      return <Alerts.AlertCircle />;
    case 'not-started':
      return <Maps.Rocket />;
    case 'active':
    default:
      return <Media.Play />;
  }
}

/**
 * A chevron button — sits to the left of the single campaign chip — that opens a
 * dropdown of every active campaign the contact is enrolled in, so the rep can
 * see there's more than one and switch which campaign the detail view (chip +
 * steps + next step) is showing. The current campaign is highlighted in the menu.
 *
 * Renders nothing when the contact has 0 or 1 active campaigns: the single chip
 * already covers that case.
 */
export function CampaignSwitcherPure({
  campaigns,
  selectedCampaignId,
  onSelectCampaign,
  className,
}: CampaignSwitcherPureProps) {
  const id = useId();
  const popoverId = `campaign-switcher-popover-${cleanId(id)}`;
  const anchorName = `--campaign-switcher-${cleanId(id)}`;
  const [isOpen, setIsOpen] = useState(false);

  // One (or zero) active campaign needs no switcher — the single chip says it all.
  if (campaigns.length <= 1) return null;

  const items = campaigns.map((campaign) => ({
    value: campaign.id,
    label: campaign.name,
    icon: stateIcon(campaign.state),
  }));

  return (
    <>
      <Button
        data-testid="campaign-switcher"
        type="button"
        size="sm"
        tone="soft"
        shape="pill"
        icon={<Arrows.ChevronDown />}
        active={isOpen}
        popoverTarget={popoverId}
        style={{ anchorName } as CSSProperties}
        tooltip={`Switch campaign (${campaigns.length})`}
        aria-label="Switch campaign"
        aria-haspopup="menu"
        // Render as the LEFT segment of a button group joined to the campaign chip:
        // round only the left corners and butt flush (gap-0) against the chip.
        className={cn('shrink-0 rounded-l-full! rounded-r-none!', className)}
      />
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        items={items}
        selectedValue={selectedCampaignId ?? undefined}
        onSelect={onSelectCampaign}
        onOpenChange={setIsOpen}
        filterable={false}
        popoverStyle={{ minWidth: '220px' }}
      />
    </>
  );
}
